// ── PROM Severity Thresholds ───────────────────────────────────────────────
//
// PHQ-9: 0-4=minimal, 5-9=mild, 10-14=moderate, 15-19=moderately severe, 20-27=severe
// GAD-7: 0-4=minimal, 5-9=mild, 10-14=moderate, 15-21=severe
// Others: no automatic severity (show score only)
//
// SECURITY: This module does NOT log or persist scores. Severity is computed
// purely client-side for display purposes.

export type Severity = 'minimal' | 'mild' | 'moderate' | 'moderately_severe' | 'severe';

/**
 * Detects PROM type from template name and returns the clinical severity level.
 * Returns null if the template is not a recognised scored PROM.
 */
export function computeSeverity(
  score: number | null | undefined,
  templateName: string | null | undefined,
): Severity | null {
  if (score == null || !templateName) return null;
  const upper = templateName.toUpperCase();

  if (upper.includes('PHQ-9') || upper.includes('PHQ9')) {
    if (score <= 4) return 'minimal';
    if (score <= 9) return 'mild';
    if (score <= 14) return 'moderate';
    if (score <= 19) return 'moderately_severe';
    return 'severe';
  }

  if (upper.includes('GAD-7') || upper.includes('GAD7')) {
    if (score <= 4) return 'minimal';
    if (score <= 9) return 'mild';
    if (score <= 14) return 'moderate';
    return 'severe';
  }

  return null;
}

/** Tailwind classes for the severity badge. */
export function getSeverityBadgeClasses(severity: Severity): string {
  switch (severity) {
    case 'minimal':
    case 'mild':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200';
    case 'moderate':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200';
    case 'moderately_severe':
    case 'severe':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200';
  }
}

/** Danish label for a severity level. */
export function getSeverityLabel(
  severity: Severity,
  t: (key: string, fallback: string) => string,
): string {
  switch (severity) {
    case 'minimal':
      return t('severity.minimal', 'Minimal');
    case 'mild':
      return t('severity.mild', 'Let');
    case 'moderate':
      return t('severity.moderate', 'Moderat');
    case 'moderately_severe':
      return t('severity.moderately_severe', 'Moderat svær');
    case 'severe':
      return t('severity.severe', 'Svær');
  }
}

/** Whether a severity level should trigger an alert (clinical threshold exceeded). */
export function isClinicalAlert(severity: Severity | null): boolean {
  return severity === 'severe' || severity === 'moderately_severe';
}
